<script setup>
import { computed, onMounted, ref } from 'vue';

const establishments = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const message = ref('');
const selectedId = ref('');
const form = ref(emptyForm());
const activeTab = ref('details');
const reminders = ref([]);
const reminderForm = ref(emptyReminderForm());
const selectedReminderId = ref('');

function emptyForm() {
  return {
    name: '',
    addressLine: '',
    city: '',
    postalCode: '',
    phone: '',
    latitude: '',
    longitude: '',
    isDefault: false,
    isActive: true,
  };
}

function emptyReminderForm() {
  return {
    name: '',
    offsetValue: 2,
    offsetUnit: 'hours',
    messageTemplate: 'Olá, {cliente}. Passando para lembrar que seu atendimento está marcado para {data}, às {horario}, em {estabelecimento}: {endereco}.',
    isActive: true,
  };
}

const selected = computed(() =>
  establishments.value.find(item => item.id === selectedId.value)
);

async function load() {
  isLoading.value = true;
  message.value = '';
  try {
    const response = await fetch('/api/superwoot/establishments?includeInactive=true');
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao carregar locais');
    establishments.value = payload.establishments || [];
  } catch (error) {
    message.value = error.message;
  } finally {
    isLoading.value = false;
  }
}

function select(item) {
  selectedId.value = item.id;
  form.value = {
    name: item.name || '',
    addressLine: item.address_line || '',
    city: item.city || '',
    postalCode: item.postal_code || '',
    phone: item.phone || '',
    latitude: item.latitude ?? '',
    longitude: item.longitude ?? '',
    isDefault: Boolean(item.is_default),
    isActive: Boolean(item.is_active),
  };
  activeTab.value = 'details';
  loadReminders();
}

function createNew() {
  selectedId.value = '';
  form.value = emptyForm();
  reminders.value = [];
  activeTab.value = 'details';
}

async function save() {
  isSaving.value = true;
  message.value = '';
  try {
    const payload = { ...form.value };
    if (selectedId.value) payload.establishmentId = selectedId.value;
    const response = await fetch('/api/superwoot/establishments', {
      method: selectedId.value ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao salvar local');
    message.value = 'Estabelecimento salvo.';
    await load();
    select(result.establishment);
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

function offsetMinutes() {
  const multipliers = { minutes: 1, hours: 60, days: 1440 };
  return Number(reminderForm.value.offsetValue) * multipliers[reminderForm.value.offsetUnit];
}

function offsetParts(minutes) {
  if (minutes % 1440 === 0) return { offsetValue: minutes / 1440, offsetUnit: 'days' };
  if (minutes % 60 === 0) return { offsetValue: minutes / 60, offsetUnit: 'hours' };
  return { offsetValue: minutes, offsetUnit: 'minutes' };
}

async function loadReminders() {
  reminders.value = [];
  if (!selectedId.value) return;
  try {
    const response = await fetch(`/api/superwoot/reminders?establishmentId=${selectedId.value}`);
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || 'Falha ao carregar lembretes');
    reminders.value = payload.reminders || [];
    const selectedReminder = reminders.value.find(item => item.id === selectedReminderId.value);
    if (selectedReminder) {
      editReminder(selectedReminder);
    } else if (reminders.value.length) {
      editReminder(reminders.value[0]);
    } else {
      createReminder();
    }
  } catch (error) {
    message.value = error.message;
  }
}

function createReminder() {
  selectedReminderId.value = '';
  reminderForm.value = emptyReminderForm();
  message.value = 'Preencha os dados da nova regra.';
}

function editReminder(reminder) {
  selectedReminderId.value = reminder.id;
  reminderForm.value = {
    name: reminder.name,
    ...offsetParts(reminder.offset_minutes),
    messageTemplate: reminder.message_template,
    isActive: reminder.is_active,
  };
}

async function saveReminder() {
  if (!selectedId.value) return;
  message.value = '';
  try {
    const payload = {
      reminderId: selectedReminderId.value || undefined,
      establishmentId: selectedId.value,
      name: reminderForm.value.name,
      offsetMinutes: offsetMinutes(),
      messageTemplate: reminderForm.value.messageTemplate,
      isActive: reminderForm.value.isActive,
    };
    const response = await fetch('/api/superwoot/reminders', {
      method: selectedReminderId.value ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao salvar lembrete');
    message.value = 'Lembrete salvo.';
    await loadReminders();
    editReminder(result.reminder);
  } catch (error) {
    message.value = error.message;
  }
}

async function toggleReminder(reminder) {
  try {
    const response = await fetch('/api/superwoot/reminders', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reminderId: reminder.id, isActive: !reminder.is_active }),
    });
    if (!response.ok) throw new Error('Falha ao atualizar lembrete');
    await loadReminders();
  } catch (error) {
    message.value = error.message;
  }
}

onMounted(load);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-n-background">
    <header class="border-b border-n-weak px-4 py-4 sm:px-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-xl font-medium text-n-slate-12">Estabelecimentos</h1>
          <p class="mt-1 text-sm text-n-slate-10">
            Locais exibidos ao cliente após a confirmação do agendamento.
          </p>
        </div>
        <button
          class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white"
          type="button"
          @click="createNew"
        >
          <span class="i-lucide-plus size-4" />
          Novo local
        </button>
      </div>
    </header>

    <div class="grid min-h-0 flex-1 gap-0 lg:grid-cols-[minmax(260px,360px)_1fr]">
      <aside class="border-b border-n-weak p-4 lg:border-b-0 lg:border-r">
        <p v-if="isLoading" class="text-sm text-n-slate-10">Carregando...</p>
        <p v-else-if="establishments.length === 0" class="text-sm text-n-slate-10">
          Nenhum estabelecimento cadastrado.
        </p>
        <div class="space-y-2">
          <button
            v-for="item in establishments"
            :key="item.id"
            type="button"
            class="w-full rounded-md border p-3 text-left transition-colors"
            :class="item.id === selectedId ? 'border-n-brand bg-n-alpha-2' : 'border-n-weak hover:bg-n-alpha-1'"
            @click="select(item)"
          >
            <span class="flex items-center justify-between gap-2">
              <strong class="text-sm text-n-slate-12">{{ item.name }}</strong>
              <span v-if="item.is_default" class="text-xs font-medium text-n-brand">Padrão</span>
            </span>
            <span class="mt-1 block text-xs text-n-slate-10">{{ item.address_line }}</span>
          </button>
        </div>
      </aside>

      <main class="overflow-y-auto p-4 sm:p-6">
        <div class="mx-auto max-w-2xl">
          <div>
            <h2 class="text-base font-medium text-n-slate-12">
              {{ selected ? 'Editar estabelecimento' : 'Novo estabelecimento' }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-10">
              O local padrão será usado quando o atendimento ainda não escolher uma unidade.
            </p>
          </div>

          <nav v-if="selected" class="mt-5 flex gap-1 overflow-x-auto border-b border-n-weak" aria-label="Configuração do estabelecimento">
            <button
              v-for="tab in [
                { id: 'details', label: 'Dados', icon: 'i-lucide-building-2' },
                { id: 'reminders', label: 'Lembretes', icon: 'i-lucide-bell-ring' },
              ]"
              :key="tab.id"
              type="button"
              class="flex items-center gap-2 border-b-2 px-3 py-2 text-sm"
              :class="activeTab === tab.id ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-10'"
              @click="activeTab = tab.id"
            >
              <span :class="tab.icon" />
              {{ tab.label }}
            </button>
          </nav>

          <form v-if="activeTab === 'details'" class="mt-5 space-y-5" @submit.prevent="save">
          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">Nome</span>
            <input v-model="form.name" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
          </label>
          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">Endereço</span>
            <input v-model="form.addressLine" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
          </label>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="block">
              <span class="mb-1 block text-sm text-n-slate-11">Cidade</span>
              <input v-model="form.city" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
            </label>
            <label class="block">
              <span class="mb-1 block text-sm text-n-slate-11">Código postal</span>
              <input v-model="form.postalCode" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
            </label>
          </div>
          <label class="block">
            <span class="mb-1 block text-sm text-n-slate-11">Telefone</span>
            <input v-model="form.phone" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
          </label>
          <div class="grid gap-4 sm:grid-cols-2">
            <label class="block">
              <span class="mb-1 block text-sm text-n-slate-11">Latitude do mapa</span>
              <input v-model="form.latitude" type="number" step="any" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="41.000000" />
            </label>
            <label class="block">
              <span class="mb-1 block text-sm text-n-slate-11">Longitude do mapa</span>
              <input v-model="form.longitude" type="number" step="any" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="-8.000000" />
            </label>
          </div>

          <div class="flex flex-wrap gap-5">
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="form.isDefault" type="checkbox" />
              Local padrão
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="form.isActive" type="checkbox" />
              Ativo
            </label>
          </div>

          <p v-if="message" class="text-sm text-n-slate-11">{{ message }}</p>
          <button
            class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white disabled:opacity-60"
            type="submit"
            :disabled="isSaving"
          >
            <span class="i-lucide-save size-4" />
            {{ isSaving ? 'Salvando...' : 'Salvar' }}
          </button>
          </form>

          <section v-else class="mt-5 space-y-5">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-medium text-n-slate-12">Lembretes da unidade</h3>
                <p class="mt-1 text-sm text-n-slate-10">O n8n envia automaticamente antes do horário marcado.</p>
              </div>
              <button type="button" class="inline-flex h-8 items-center gap-2 rounded-md border border-n-weak px-3 text-sm text-n-slate-11" @click="createReminder">
                <span class="i-lucide-plus size-4" />
                Nova regra
              </button>
            </div>

            <div class="space-y-2">
              <article
                v-for="reminder in reminders"
                :key="reminder.id"
                class="flex items-center justify-between gap-3 rounded-md border p-3"
                :class="reminder.id === selectedReminderId ? 'border-n-brand bg-n-alpha-2' : 'border-n-weak bg-n-alpha-1'"
              >
                <button type="button" class="min-w-0 text-left" @click="editReminder(reminder)">
                  <strong class="block truncate text-sm text-n-slate-12">{{ reminder.name }}</strong>
                  <span class="mt-1 block text-xs text-n-slate-10">{{ reminder.offset_minutes }} minutos antes</span>
                </button>
                <button type="button" class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-11" @click="toggleReminder(reminder)">
                  {{ reminder.is_active ? 'Ativo' : 'Inativo' }}
                </button>
              </article>
            </div>

            <form class="space-y-4 rounded-md border border-n-weak p-4" @submit.prevent="saveReminder">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ selectedReminderId ? 'Editar regra' : 'Nova regra' }}
              </h3>
              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">Nome da regra</span>
                <input v-model="reminderForm.name" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
              </label>
              <div>
                <span class="mb-1 block text-sm text-n-slate-11">Enviar antes do agendamento</span>
                <div class="grid grid-cols-[1fr_140px] gap-2">
                  <input v-model.number="reminderForm.offsetValue" required min="1" type="number" class="h-10 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" />
                  <select v-model="reminderForm.offsetUnit" class="h-10 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm">
                    <option value="minutes">Minutos</option>
                    <option value="hours">Horas</option>
                    <option value="days">Dias</option>
                  </select>
                </div>
              </div>
              <label class="block">
                <span class="mb-1 block text-sm text-n-slate-11">Mensagem</span>
                <textarea v-model="reminderForm.messageTemplate" required rows="5" class="w-full resize-y rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" />
              </label>
              <p class="text-xs text-n-slate-10">
                Variáveis: {cliente}, {data}, {horario}, {profissional}, {estabelecimento}, {endereco}
              </p>
              <label class="flex items-center gap-2 text-sm text-n-slate-11">
                <input v-model="reminderForm.isActive" type="checkbox" />
                Regra ativa
              </label>
              <button type="submit" class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white">
                <span class="i-lucide-save size-4" />
                Salvar lembrete
              </button>
            </form>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>
